import React, { useState, useEffect, useRef } from 'react';
import { useStomp } from '../../contexts/StompContext';
import MessageBubble from './MessageBubble';
import MessageInput from './MessageInput';
import ConversationSettings from './ConversationSettings';

const ChatWindow = ({ conversation, currentUser, onConversationUpdate }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [page, setPage] = useState(1);
  const [showSettings, setShowSettings] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const { addMessageHandler } = useStomp();

  // Helper function to safely get conversation ID
  const getConversationId = (conv) => {
    if (!conv) return null;
    
    // Try different possible ID structures
    const possibleIds = [
      conv._id?.$oid,    // MongoDB ObjectId format
      conv._id,          // Direct _id
      conv.id?.$oid,     // Alternative ObjectId format  
      conv.id,           // Direct id
      conv['_id']?.$oid, // Bracket notation ObjectId
      conv['_id'],       // Bracket notation _id
      conv['id']         // Bracket notation id
    ];
    
    const conversationId = possibleIds.find(id => id && id !== 'undefined' && typeof id === 'string');
    
    if (!conversationId) {
      console.error('❌ ChatWindow: Could not find valid conversation ID in:', conv);
      console.error('❌ ChatWindow: Tried these values:', possibleIds);
    }
    
    return conversationId;
  };

  useEffect(() => {
    if (conversation) {
      console.log('🔍 ChatWindow: Conversation object received:', conversation);
      console.log('🔍 ChatWindow: Conversation keys:', Object.keys(conversation));
      console.log('🔍 ChatWindow: Conversation._id:', conversation._id);
      console.log('🔍 ChatWindow: Conversation.id:', conversation.id);
      
      // Try different ID access patterns
      const possibleIds = [
        conversation._id?.$oid,
        conversation._id,
        conversation.id?.$oid,
        conversation.id,
        conversation['_id']?.$oid,
        conversation['_id'],
        conversation['id']
      ];
      
      console.log('🔍 ChatWindow: Possible ID values:', possibleIds);
      
      // Find the actual conversation ID
      const conversationId = possibleIds.find(id => id && id !== 'undefined');
      console.log('🔍 ChatWindow: Using conversation ID:', conversationId);
    }
  }, [conversation]);

  useEffect(() => {
    if (conversation) {
      loadMessages(true); // Reset and load fresh messages
      
      // Subscribe to chat messages for this conversation
      const handleChatMessage = (message, topic) => {
        console.log('STOMP: Chat window received message', message);
        
        // Debug conversation ID comparison
        const stompConversationId = message.conversation?._id?.$oid || message.conversation?._id || message.conversation?.id;
        const currentConversationId = getConversationId(conversation);
        
        console.log('🔍 STOMP: Conversation ID comparison:');
        console.log('🔍 STOMP: STOMP conversation ID:', stompConversationId);
        console.log('🔍 STOMP: Current conversation ID:', currentConversationId);
        console.log('🔍 STOMP: IDs match:', stompConversationId === currentConversationId);
        console.log('🔍 STOMP: Message has content:', !!message.message);
        console.log('🔍 STOMP: Full message object:', message);
        
        if (message.message && stompConversationId === currentConversationId) {
          const receivedMessage = message.message;
          
          console.log('✅ STOMP: Message is for current conversation');
          console.log('✅ STOMP: Message details:', {
            messageId: receivedMessage._id,
            sender: receivedMessage.user?.username,
            currentUser: currentUser.username,
            isSenderConfirmation: message.is_sender_confirmation,
            body: receivedMessage.body?.substring(0, 50)
          });
          
          // Check if this is a sender confirmation
          if (message.is_sender_confirmation && receivedMessage.user.username === currentUser.username) {
            console.log('🔄 STOMP: Received sender confirmation for message', receivedMessage._id.$oid);
            console.log('🔄 STOMP: Looking for message to confirm in current messages:', messages.length);
            
            // Update the message in the list to confirmed state
            setMessages(prevMessages => {
              console.log('🔄 STOMP: Current messages before update:', prevMessages.map(m => ({
                id: m._id?.$oid || m._id,
                body: m.body?.substring(0, 20),
                isPending: m.isPending,
                isConfirmed: m.isConfirmed
              })));
              
              const updatedMessages = prevMessages.map(msg => {
                const msgId = msg._id?.$oid || msg._id;
                const receivedId = receivedMessage._id?.$oid || receivedMessage._id;
                
                console.log('🔄 STOMP: Comparing message IDs:', { msgId, receivedId, match: msgId === receivedId });
                
                if (msgId === receivedId) {
                  console.log('✅ STOMP: Found matching message, marking as confirmed');
                  return { 
                    ...receivedMessage, 
                    isConfirmed: true, 
                    isPending: false 
                  };
                }
                
                // Also check if this is a pending message with the same body and user
                // (in case the optimistic message hasn't been replaced with the real one yet)
                if (msg.isPending && 
                    msg.user.username === currentUser.username && 
                    msg.body === receivedMessage.body &&
                    Math.abs(new Date(msg.created_at) - new Date(receivedMessage.created_at)) < 5000) {
                  console.log('✅ STOMP: Found matching pending message by content, marking as confirmed');
                  return { 
                    ...receivedMessage, 
                    isConfirmed: true, 
                    isPending: false 
                  };
                }
                
                return msg;
              });
              
              console.log('🔄 STOMP: Messages after confirmation update:', updatedMessages.map(m => ({
                id: m._id?.$oid || m._id,
                body: m.body?.substring(0, 20),
                isPending: m.isPending,
                isConfirmed: m.isConfirmed
              })));
              
              return updatedMessages;
            });
          } else {
            // Regular message from another user
            console.log('📨 STOMP: Processing regular message from another user:', receivedMessage);
            console.log('📨 STOMP: Received message structure:', {
              id: receivedMessage._id,
              user: receivedMessage.user?.username,
              body: receivedMessage.body?.substring(0, 50)
            });
            
            setMessages(prevMessages => {
              console.log('📨 STOMP: Current messages count before adding:', prevMessages.length);
              
              // Check if message already exists (avoid duplicates)
              // Handle different ID structures
              const receivedId = receivedMessage._id?.$oid || receivedMessage._id;
              const exists = prevMessages.some(msg => {
                const msgId = msg._id?.$oid || msg._id;
                return msgId === receivedId;
              });
              
              if (exists) {
                console.log('📨 STOMP: Message already exists, skipping duplicate');
                return prevMessages;
              }
              
              console.log('📨 STOMP: Adding new message to conversation');
              const updatedMessages = [...prevMessages, receivedMessage];
              console.log('📨 STOMP: Messages count after adding:', updatedMessages.length);
              return updatedMessages;
            });
            // scrollToBottom will be handled by useEffect when messages change
          }
          
          // Mark messages as read
          markMessagesAsRead();
        } else {
          console.log('❌ STOMP: Message not for current conversation or missing data');
          
          // Fallback: Check if this is a sender confirmation for our user regardless of conversation ID
          if (message.is_sender_confirmation && 
              message.message && 
              message.message.user.username === currentUser.username) {
            console.log('🔄 STOMP: Processing sender confirmation as fallback (conversation ID mismatch)');
            
            const receivedMessage = message.message;
            
            console.log('🔄 STOMP: Fallback - Received sender confirmation for message', receivedMessage._id?.$oid || receivedMessage._id);
            console.log('🔄 STOMP: Fallback - Looking for message to confirm in current messages:', messages.length);
            
            // Update the message in the list to confirmed state
            setMessages(prevMessages => {
              console.log('🔄 STOMP: Fallback - Current messages before update:', prevMessages.map(m => ({
                id: m._id?.$oid || m._id,
                body: m.body?.substring(0, 20),
                isPending: m.isPending,
                isConfirmed: m.isConfirmed
              })));
              
              const updatedMessages = prevMessages.map(msg => {
                const msgId = msg._id?.$oid || msg._id;
                const receivedId = receivedMessage._id?.$oid || receivedMessage._id;
                
                console.log('🔄 STOMP: Fallback - Comparing message IDs:', { msgId, receivedId, match: msgId === receivedId });
                
                if (msgId === receivedId) {
                  console.log('✅ STOMP: Fallback - Found matching message, marking as confirmed');
                  return { 
                    ...receivedMessage, 
                    isConfirmed: true, 
                    isPending: false 
                  };
                }
                
                return msg;
              });
              
              console.log('🔄 STOMP: Fallback - Messages after confirmation update:', updatedMessages.map(m => ({
                id: m._id?.$oid || m._id,
                body: m.body?.substring(0, 20),
                isPending: m.isPending,
                isConfirmed: m.isConfirmed
              })));
              
              return updatedMessages;
            });
          }
        }
      };

      // Find current user's conversation_user record to get their stomp_key for this conversation
      const currentUserConversationUser = conversation.conversation_users?.find(cu => 
        cu.user?.username === currentUser.username || cu.user?._id === currentUser._id
      );
      
      if (!currentUserConversationUser || !currentUserConversationUser.stomp_key) {
        console.error('❌ ChatWindow: Cannot find conversation_user or stomp_key for current user');
        console.log('🔍 ChatWindow: Available conversation_users:', conversation.conversation_users?.map(cu => ({
          username: cu.user?.username,
          stomp_key: cu.stomp_key
        })));
        // Fallback to general chat topic if conversation_user not found
        const cleanup = addMessageHandler('chat', handleChatMessage);
        return cleanup;
      }
      
      const conversationTopic = `chat_q/user_${currentUserConversationUser.stomp_key}`;
      console.log('🔔 ChatWindow: Subscribing to conversation topic:', conversationTopic);

      const cleanup = addMessageHandler(conversationTopic, handleChatMessage);

      return cleanup;
    }
  }, [getConversationId(conversation), currentUser.stomp_key, addMessageHandler]);

  const loadMessages = async (reset = false) => {
    if (!conversation) return;
    
    try {
      const conversationId = getConversationId(conversation);
      if (!conversationId) {
        console.error('❌ ChatWindow: Cannot load messages - no valid conversation ID');
        return;
      }
      
      setLoading(true);
      const currentPage = reset ? 1 : page;
      const response = await fetch(
        `/api/v1/conversations/${conversationId}/messages?page=${currentPage}&per_page=40`
      );
      const data = await response.json();
      
      if (data.messages) {
        const newMessages = data.messages.reverse(); // API returns newest first, we want oldest first
        
        if (reset) {
          setMessages(newMessages);
          setPage(2);
          setTimeout(scrollToBottom, 100); // Scroll to bottom for new conversation
          
          // Update global unread count if provided in response
          if (data.meta && typeof data.meta.unread_message_count !== 'undefined') {
            console.log('📬 ChatWindow: Updating global unread count from messages API:', data.meta.unread_message_count);
            if (window.updateUnreadCount) {
              window.updateUnreadCount(data.meta.unread_message_count);
            }
          }
        } else {
          // Prepend older messages
          setMessages(prevMessages => [...newMessages, ...prevMessages]);
          setPage(prev => prev + 1);
        }
        
        setHasMoreMessages(data.messages.length === 40);
        
        // Mark messages as read
        markMessagesAsRead();
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const markMessagesAsRead = async () => {
    // This would typically be handled by the backend when fetching messages
    // The backend already handles unread counts in the MessageViewer component
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    } else if (messagesContainerRef.current) {
      // Fallback: scroll container to bottom
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Auto-scroll when messages change (new message added)
  useEffect(() => {
    // Only scroll if we're not loading older messages and there are messages
    if (!loading && messages.length > 0) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(scrollToBottom, 100);
    }
  }, [messages.length, loading]);

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop } = messagesContainerRef.current;
      
      // Load more messages when scrolled to top
      if (scrollTop === 0 && hasMoreMessages && !loading) {
        loadMessages();
      }
    }
  };

  const handleRetryMessage = async (failedMessage) => {
    // Remove the failed message and retry sending
    setMessages(prevMessages => 
      prevMessages.filter(msg => msg._id.$oid !== failedMessage._id.$oid)
    );
    
    // Retry sending the message
    await handleSendMessage(failedMessage.body, failedMessage.post);
  };

  const handleSendMessage = async (messageText, attachedPost = null) => {
    if (!messageText.trim() && !attachedPost) return;
    
    // Create optimistic message object
    const optimisticMessage = {
      _id: { $oid: `temp_${Date.now()}_${Math.random()}` },
      body: messageText,
      user: currentUser,
      conversation_user: null,
      created_at: new Date().toISOString(),
      isPending: true,
      isConfirmed: false
    };
    
    if (attachedPost) {
      optimisticMessage.post = attachedPost;
    }
    
    // Add optimistic message to UI immediately
    setMessages(prevMessages => [...prevMessages, optimisticMessage]);
    // scrollToBottom will be handled by useEffect when messages change
    
    try {
      const conversationId = getConversationId(conversation);
      if (!conversationId) {
        console.error('❌ ChatWindow: Cannot send message - no valid conversation ID');
        return;
      }
      
      const formData = new FormData();
      formData.append('message[body]', messageText);
      
      if (attachedPost) {
        formData.append('message[post_id]', attachedPost._id.$oid);
      }

      const response = await fetch(
        `/api/v1/conversations/${conversationId}/messages`,
        {
          method: 'POST',
          body: formData
        }
      );

      const data = await response.json();
      
      if (data.message) {
        // Replace optimistic message with real message
        const realMessage = data.message;
        
        console.log('📤 API: Replacing optimistic message with real message');
        console.log('📤 API: Optimistic ID:', optimisticMessage._id.$oid);
        console.log('📤 API: Real message ID:', realMessage._id?.$oid || realMessage._id);
        console.log('📤 API: Real message body:', realMessage.body?.substring(0, 20));
        
        setMessages(prevMessages => {
          const updatedMessages = prevMessages.map(msg => 
            msg._id.$oid === optimisticMessage._id.$oid 
              ? { ...realMessage, isPending: true, isConfirmed: false } // Still pending until STOMP confirmation
              : msg
          );
          
          console.log('📤 API: Messages after replacement:', updatedMessages.map(m => ({
            id: m._id?.$oid || m._id,
            body: m.body?.substring(0, 20),
            isPending: m.isPending,
            isConfirmed: m.isConfirmed
          })));
          
          return updatedMessages;
        });
        
        // scrollToBottom will be handled by useEffect when messages change
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Mark optimistic message as failed
      setMessages(prevMessages => 
        prevMessages.map(msg => 
          msg._id.$oid === optimisticMessage._id.$oid 
            ? { ...msg, isFailed: true, isPending: false }
            : msg
        )
      );
    }
  };

  const getConversationTitle = () => {
    if (conversation.title && conversation.title.trim()) {
      return conversation.title;
    }
    
    const otherUsers = conversation.users?.filter(u => u.username !== currentUser.username) || [];
    if (otherUsers.length === 0) return 'Conversa';
    if (otherUsers.length === 1) return otherUsers[0].username;
    if (otherUsers.length === 2) return `${otherUsers[0].username}, ${otherUsers[1].username}`;
    return `${otherUsers[0].username} e mais ${otherUsers.length - 1}`;
  };

  const getOtherUsers = () => {
    return conversation.users?.filter(u => u.username !== currentUser.username) || [];
  };

  const handleLeaveConversation = async () => {
    try {
      const conversationId = getConversationId(conversation);
      if (!conversationId) {
        console.error('❌ ChatWindow: Cannot leave conversation - no valid conversation ID');
        return;
      }
      
      await fetch(`/api/v1/conversations/${conversationId}`, {
        method: 'DELETE'
      });
      
      // Conversation will be removed from list by parent component
      onConversationUpdate(null);
    } catch (error) {
      console.error('Error leaving conversation:', error);
    }
  };

  const handleUpdateConversation = async (updates) => {
    try {
      const formData = new FormData();
      
      if (updates.title !== undefined) {
        formData.append('conversation[title]', updates.title);
      }
      
      if (updates.users) {
        updates.users.forEach(username => {
          formData.append('conversation[neighbors][]', username);
        });
      }

      const conversationId = getConversationId(conversation);
      if (!conversationId) {
        console.error('❌ ChatWindow: Cannot update conversation - no valid conversation ID');
        return;
      }

      const response = await fetch(`/api/v1/conversations/${conversationId}`, {
        method: 'PUT',
        body: formData
      });

      const data = await response.json();
      
      if (data.conversation) {
        onConversationUpdate(data.conversation);
      }
    } catch (error) {
      console.error('Error updating conversation:', error);
    }
  };

  if (!conversation) {
    return (
      <div className="chat-window">
        <div className="no-conversation">
          <p>Selecione uma conversa</p>
        </div>
      </div>
    );
  }

  const otherUsers = getOtherUsers();
  const isGroupChat = otherUsers.length > 1;

  return (
    <div className="chat-window">
      <div className="chat-header">
        <div className="chat-header-info">
          <h3>{getConversationTitle()}</h3>
          <div className="chat-header-details">
            {isGroupChat ? (
              <span>{conversation.users?.length || 0} participantes</span>
            ) : otherUsers.length > 0 ? (
              <span>@{otherUsers[0].username}</span>
            ) : (
              <span>Conversa</span>
            )}
          </div>
        </div>
        
        <div className="chat-header-actions">
          <button 
            className="settings-button"
            onClick={() => setShowSettings(!showSettings)}
            title="Configurações da conversa"
          >
            ⚙️
          </button>
        </div>
      </div>

      {showSettings && (
        <ConversationSettings
          conversation={conversation}
          currentUser={currentUser}
          onClose={() => setShowSettings(false)}
          onLeaveConversation={handleLeaveConversation}
          onUpdateConversation={handleUpdateConversation}
        />
      )}

      <div 
        className="messages-container"
        ref={messagesContainerRef}
        onScroll={handleScroll}
      >
        {loading && page === 1 && (
          <div className="loading-messages">
            <p>Carregando mensagens...</p>
          </div>
        )}
        
        {loading && page > 1 && (
          <div className="loading-more-messages">
            <p>Carregando mais mensagens...</p>
          </div>
        )}
        
        {!hasMoreMessages && messages.length > 40 && (
          <div className="conversation-start">
            <p>Início da conversa</p>
          </div>
        )}

        <div className="messages-list">
          {messages.map((message, index) => {
            const prevMessage = index > 0 ? messages[index - 1] : null;
            const showAvatar = !prevMessage || 
              prevMessage.user.username !== message.user.username ||
              (new Date(message.created_at) - new Date(prevMessage.created_at)) > 300000; // 5 minutes

            return (
              <MessageBubble
                key={message._id.$oid}
                message={message}
                currentUser={currentUser}
                showAvatar={showAvatar}
                onRetryMessage={handleRetryMessage}
              />
            );
          })}
        </div>
        
        <div ref={messagesEndRef} />
      </div>

      <MessageInput
        onSendMessage={handleSendMessage}
        disabled={loading}
      />
    </div>
  );
};

export default ChatWindow;
